import { useMemo } from 'react';
import { useFilterState } from './hooks/UseFilterState';
import { createFilterDefinitions } from './config/FilterDefinitions';
import { DesktopFilterSidebar } from './layouts/DesktopFilterSideBar';
import { TabletFilterBar } from './layouts/TabletFilterBar';
import { TabletInlineFilters } from './layouts/TabletInlineFilters';
import type { DynamicRanges } from './types';
import { locationSearchText, useLocationLabel } from '../../../utils/locationLabel';

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
	/** Data sources for filter options */
	shapes: any[];
	categories: any[];
	types: any[];
	locations: any[];
	buildings?: any[];

	/** URL state management */
	queryParams: URLSearchParams;
	navigate: (opts: { search: string }) => void;

	/** Dynamic ranges from the server (used to set slider min/max) */
	heightRange?: { min: number; max: number };
	widthRange?: { min: number; max: number };
	outerDiameterRange?: { min: number; max: number };
	innerDiameterRange?: { min: number; max: number };
	wallThicknessRange?: { min: number; max: number };
	currentLengthRange?: { min: number; max: number };

	/**
	 * Which layout to render:
	 * - 'desktop': vertical sidebar with collapsible panels
	 * - 'tablet': horizontal bar with chips + drawer
	 * - 'tablet-inline': horizontal inline filters, all visible (no drawer)
	 */
	layout?: 'desktop' | 'tablet' | 'tablet-inline';

	/**
	 * Override which filters are shown (by key).
	 * If not provided, all filters for the given context are shown.
	 */
	visibleFilterKeys?: string[];

	/**
	 * For tablet layout: which filters appear as quick-access chips
	 */
	quickAccessFilters?: string[];

	/**
	 * For tablet-inline layout: group filters together in shared containers.
	 * Example: [{ label: 'Dimensions', keys: ['height','width','wall_thickness'] }]
	 */
	filterGroups?: Array<{ label: string; keys: string[] }>;

	/** Whether the value summary is shown (sum of value for filtered lengths). Controlled by "Show Value" on left panel. */
	showValueSummary?: boolean;
	/** Toggle handler for the Show Value switch */
	onToggleValueSummary?: (value: boolean) => void;

	/** For tablet-inline: whether filters are expanded */
	filtersExpanded?: boolean;
	/** For tablet-inline: toggle expand/collapse */
	onToggleFiltersExpanded?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const MaterialLengthsFilter = ({
	shapes,
	categories,
	types,
	locations,
	buildings = [],
	queryParams,
	navigate,
	heightRange,
	widthRange,
	outerDiameterRange,
	innerDiameterRange,
	wallThicknessRange,
	currentLengthRange,
	layout = 'desktop',
	visibleFilterKeys,
	quickAccessFilters = ['shape', 'status', 'height', 'current_length'],
	filterGroups = [],
	showValueSummary = false,
	onToggleValueSummary,
	filtersExpanded,
	onToggleFiltersExpanded,
}: Props) => {
	const formatLocation = useLocationLabel();
	// ── Build shape ID map for visibility conditions ─────────────────────────
	const shapeIdMap = useMemo(
		() =>
			shapes.reduce((acc: Record<string, string>, s: any) => {
				acc[s.display_name] = s.id;
				return acc;
			}, {}),
		[shapes]
	);

	// ── Create filter definitions (memoized) ────────────────────────────────
	const definitions = useMemo(() => {
		let defs = createFilterDefinitions(shapeIdMap);

		if (buildings.length <= 1) {
			defs = defs.filter((d) => d.key !== 'building');
		}

		// If caller specified which filters to show, filter the definitions
		if (visibleFilterKeys) {
			defs = defs.filter((d) => visibleFilterKeys.includes(d.key));
		}

		return defs;
	}, [shapeIdMap, visibleFilterKeys, buildings.length]);

	// ── Build dynamic ranges map ────────────────────────────────────────────
	const dynamicRanges: DynamicRanges = useMemo(
		() => ({
			heightRange,
			widthRange,
			outerDiameterRange,
			innerDiameterRange,
			wallThicknessRange,
			currentLengthRange,
			// Available length uses 0 to max of currentLength
			availableLengthRange: currentLengthRange
				? { min: 0, max: currentLengthRange.max }
				: undefined,
		}),
		[
			heightRange,
			widthRange,
			outerDiameterRange,
			innerDiameterRange,
			wallThicknessRange,
			currentLengthRange,
		]
	);

	// ── Initialize the central hook ─────────────────────────────────────────
	const filterState = useFilterState({
		definitions,
		queryParams,
		navigate,
		dynamicRanges,
	});

	// ── Resolve dynamic options from props ───────────────────────────────────
	const resolvedOptions = useMemo(
		() => ({
			shape: shapes.map((s: any) => ({
				value: s.id,
				label: s.display_name,
			})),
			category: categories.map((c: any) => ({
				value: c.id,
				label: c.display_name,
			})),
			types: types.map((t: any) => ({
				value: t.id,
				label: t.display_name,
			})),
			building: buildings.map((b: any) => ({
				value: b.id,
				label: b.name,
			})),
		}),
		[shapes, categories, types, buildings]
	);

	// ── Resolve location option groups (grouped by rack) ────────────────────
	const resolvedOptionGroups = useMemo(() => {
		const grouped: Record<string, any[]> = {};
		for (const location of locations || []) {
			const rackName = location.shelf?.rack?.rack_name || 'Unknown Rack';
			if (!grouped[rackName]) grouped[rackName] = [];
			grouped[rackName].push(location);
		}
		return {
			inventory_location: Object.entries(grouped).map(([label, items]) => ({
				label,
				options: items.map((loc: any) => ({
					value: loc.id,
					label: formatLocation(loc.display_name),
					searchText: locationSearchText(loc.display_name, formatLocation),
				})),
			})),
		};
	}, [locations, formatLocation]);

	// ── Reset handler ────────────────────────────────────────────────────────
	const handleResetAll = () => {
		// "All shapes" = no shape param in URL (backend returns all when shape absent)
		filterState.resetAll();
	};

	// ── Render the appropriate layout ────────────────────────────────────────
	if (layout === 'tablet') {
		return (
			<TabletFilterBar
				definitions={definitions}
				filterState={filterState}
				resolvedOptions={resolvedOptions}
				resolvedOptionGroups={resolvedOptionGroups}
				onResetAll={handleResetAll}
				quickAccessFilters={quickAccessFilters}
			/>
		);
	}

	if (layout === 'tablet-inline') {
		return (
			<TabletInlineFilters
				definitions={definitions}
				filterState={filterState}
				resolvedOptions={resolvedOptions}
				resolvedOptionGroups={resolvedOptionGroups}
				onResetAll={handleResetAll}
				groups={filterGroups}
				expanded={filtersExpanded}
				onToggleExpanded={onToggleFiltersExpanded}
			/>
		);
	}

	return (
		<DesktopFilterSidebar
			definitions={definitions}
			filterState={filterState}
			resolvedOptions={resolvedOptions}
			resolvedOptionGroups={resolvedOptionGroups}
			onResetAll={handleResetAll}
			showValueSummary={showValueSummary}
			onToggleValueSummary={onToggleValueSummary}
		/>
	);
};
